import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Prevent body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navLinks = [
    { label: '關於我們', to: '/' },
    { label: '年度計畫', to: '/annual-plan' },
    { label: '組織架構', to: '/leadership' },
    { label: '會員名冊', to: '/members' },
    { label: '加入/聯絡我們', to: '/contact' },
  ];

  const isActive = (to: string) => location.pathname === to;
  const isHome = location.pathname === '/';
  const showWhiteBg = !isHome || isScrolled || menuOpen;

  return (
    <>
      <nav
        ref={menuRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showWhiteBg ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/97ed753d-1739-4e4c-986b-6369dc88e0ce.png"
                alt="YMC Taipei Logo"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover"
              />
              <span className="text-sm font-bold hidden sm:block text-emerald-900">
                YMC Taipei
              </span>
            </Link>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-bold transition-colors relative group whitespace-nowrap ${
                    isActive(link.to)
                      ? 'text-purple-900'
                      : 'text-gray-700 hover:text-purple-900'
                  }`}
                >
                  {link.label}
                  <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-gradient-to-r from-purple-600 to-orange-500 transition-all duration-300 ${
                    isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <a
              href="https://lin.ee/pVaOePX"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold transition-colors duration-200 whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center"></i>
              加入我們
            </a>

            {/* Hamburger Button */}
            <button
              className={`md:hidden relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors cursor-pointer ${
                showWhiteBg ? 'hover:bg-gray-100' : 'hover:bg-white/20'
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? '關閉選單' : '開啟選單'}
            >
              {/* Animated hamburger icon */}
              <div className="w-5 h-4 flex flex-col justify-between">
                <span className={`block h-0.5 rounded-full transition-all duration-300 origin-center bg-gray-700 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}></span>
                <span className={`block h-0.5 rounded-full transition-all duration-300 bg-gray-700 ${menuOpen ? 'opacity-0 scale-x-0' : ''}`}></span>
                <span className={`block h-0.5 rounded-full transition-all duration-300 origin-center bg-gray-700 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu — smooth slide down */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          menuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-white/98 backdrop-blur-md border-t border-gray-100 px-4 py-4">
            <nav className="space-y-1">
              {navLinks.map((link, i) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center justify-between px-4 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isActive(link.to)
                      ? 'bg-purple-50 text-purple-900'
                      : 'text-gray-700 hover:bg-stone-50 hover:text-purple-900'
                  }`}
                  style={{ transitionDelay: menuOpen ? `${i * 40}ms` : '0ms' }}
                >
                  <span>{link.label}</span>
                  {isActive(link.to) && (
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-600 flex-shrink-0"></span>
                  )}
                </Link>
              ))}
            </nav>

            {/* Mobile CTA */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <a
                href="https://lin.ee/pVaOePX"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-700 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center"></i>
                加入我們
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Backdrop overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}
